import platform from '@play-co/gcinstant';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import statePromise from 'src/lib/statePromise';
import { waitForAssets, getCreativeSuffix } from 'src/lib/utils';
import { petSpeciesAssets } from 'src/loadingGroups';
import { Pet } from 'src/replicant/buruburu/pet';
import StateObserver from 'src/StateObserver';
import { getPet } from '.';
import { PetAppearanceController } from '../appearance/PetAppearanceController';
import { careActions } from '../care/care.redux';
import { levelUpSequence } from '../level/level.sequence';
import {
  conversationActions,
  ConversationState,
} from '../conversation/conversation.redux';
import { levelActions, LevelState } from '../level/level.redux';
import { EntityManager, fromEntityId, PetEntity } from '../logic/EntityManager';
import SceneManager from '../logic/SceneManager';
import { PetPositionController } from '../position/PetPositionController';
import { Position } from '../position/PositionModel';
import { petActions, PetState } from './pet.redux';
import { Visit } from 'src/replicant/visit';

export class PetController {
  public readonly id: string;
  public readonly position: PetPositionController;
  public readonly appearance: PetAppearanceController;

  /**
   * Return pet status of current pet
   */
  public get status() {
    const { user, pet } = StateObserver.getState();
    const isVisiting = Visit.getters(user).isVisiting();
    const now = StateObserver.now();

    return this.id === platform.playerID
      ? Pet.getters(user).getPetState(now, isVisiting) // Player status
      : Pet.ruleset.getPetState(
          pet[this.id].attributes,
          pet[this.id].statusEffects,
          isVisiting,
        ); // Friend status
  }

  public get species() {
    const { user, pet } = StateObserver.getState();
    return this.id === platform.playerID
      ? Pet.getters(user).getCurrentEvolution()?.species // Get species from replicant for local player.
      : pet[this.id].species;
  }

  public get stage() {
    return Pet.ruleset.evolutions[this.species].stage;
  }

  private evolving = false;

  constructor(sceneManager: SceneManager, private entity: PetEntity) {
    this.id = fromEntityId(entity.entityId).id;
    this.position = new PetPositionController(sceneManager, entity);
    this.appearance = new PetAppearanceController(entity);
  }

  public init(initialState?: {
    conversation?: Partial<ConversationState>;
    pet?: Partial<PetState>;
    level?: Partial<LevelState>;
    position?: Position;
  }) {
    const { createLevel } = levelActions;
    const { createConversation } = conversationActions;
    const { id } = this;

    StateObserver.dispatch(
      petActions.createPet({ id, payload: initialState?.pet ?? {} }),
    );

    StateObserver.dispatch(
      createConversation({
        id,
        payload: initialState?.conversation ?? {},
      }),
    );

    StateObserver.dispatch(
      createLevel({
        id,
        payload: initialState?.level ?? {},
      }),
    );

    this.position.init(initialState?.position ?? null);
    this.appearance.init();
  }

  /**
   * Hide pet movieclip
   */
  public hide() {
    this.entity.hide();
  }

  /**
   * Show pet movieclip
   */

  public show() {
    this.entity.show();
  }

  public destroy() {
    this.position.destroy();

    const { id } = this;

    StateObserver.dispatch(petActions.deletePet(id));
    StateObserver.dispatch(levelActions.deleteLevel(id));
    StateObserver.dispatch(conversationActions.deleteConversation(id));

    EntityManager.deleteEntity(this.entity.entityId);
  }

  public async moveTo(position: Position | null, duration?: number) {
    const currentScene = this.position.position?.scene;
    if (currentScene && position.scene !== currentScene) {
      this.appearance.setAction('hang');
    }
    await this.position.moveTo(position, duration);
    this.appearance.setAction(null);
  }

  public async handleEvolving() {
    // Do not evolve if there is no evolving status effect
    if (
      !Pet.getters(StateObserver.getState().user).hasStatusEffect(
        'evolving',
        StateObserver.now(),
      )
    )
      return;

    if (this.evolving) return;

    this.evolving = true;

    await statePromise(
      ({ conversation }) =>
        conversation[platform.playerID].activeDialog === null,
    );

    StateObserver.dispatch(careActions.setCareAction({ type: 'evolve' }));
    await statePromise(({ pet }) => pet[platform.playerID].action === 'evolve');
    await StateObserver.invoke.evolvePet();

    const { user } = StateObserver.getState();
    const newSpecies = Pet.getters(user).getCurrentEvolution()?.species;
    if (newSpecies) {
      const pet = EntityManager.createOrGetPlayerPet();
      await Promise.all([
        pet.setEvolutionEffectsVisible(true),
        waitForAssets(petSpeciesAssets[newSpecies]),
      ]);

      getPet().appearance.setSpecies(newSpecies);

      const evolution = Pet.ruleset.evolutions[newSpecies];
      const message = evolution[`greetings_${getCreativeSuffix()}`];
      await pet.setEvolutionEffectsVisible(false);
      await openPopupPromise('popupSpeechBubble', { message });
      if (StateObserver.getState().care.currentCareAction?.type === 'evolve') {
        StateObserver.dispatch(careActions.setCareAction(null));
      }
    }

    // Show level up sequence if needed.
    await levelUpSequence();

    this.evolving = false;
  }
}
